import { parseOrderQuery } from "@logiful/domain";
import { ChannelMark } from "@/components/ChannelLogo";
import { keepPreviousData, useQuery, useQueryClient } from "@tanstack/react-query";
import { CircleAlert, CircleArrowRight, CircleCheck, CircleDashed, CirclePause, CircleX, ExternalLink, FileUp, Loader2, PackageCheck, Plus, RotateCcw, Search, SearchX, ShoppingCart, Store, Truck, Workflow, X, Split, MapPinCheck, Layers, Repeat, ChevronDown } from "lucide-react";
import { Fragment, useEffect, useMemo, useRef, useState, type KeyboardEvent } from "react";
import { useSearchParams } from "react-router";
import { PageHeader, usePermission, useTenant } from "@/components/AppShell";
import { Highlight } from "@/components/Highlight";
import { ItemsPopover } from "@/components/ItemsPopover";
import { OrderKpiStrip } from "@/components/OrderKpiStrip";
import { ManualOrderSheet } from "@/components/ManualOrderSheet";
import { OrderHistoryImportBanner, OrderHistoryImportButton } from "@/components/OrderHistoryImport";
import { OrderSheet } from "@/components/OrderSheet";
import { CsvImportDialog } from "@/components/CsvImportDialog";
import { Badge, Button, Card, cx, EmptyState, Input, Kbd, Skeleton, StatusPill, Tip, type PillTone } from "@/components/ui";
import { api, canManage, HOLD_KIND_LABEL, type HoldKind, type OrderItem, type OrderSearchQuery, type OrderStatus, type DeliveryStatus } from "@/lib/api";
import { ColumnPicker, useColumnChoice } from "@/components/ColumnPicker";
import { ORDER_COLUMN_BY_KEY, ORDER_COLUMN_DEFAULTS, ORDER_COLUMN_KEYS, ORDER_COLUMNS, type OrderColumnKey } from "@/components/OrderColumns";
import { OrderBulkBar } from "@/components/OrderBulkBar";
import { DELIVERY_VIEW } from "@/lib/delivery";
import { countryName, fmtMoney, fmtRelative, trackingUrl } from "@/lib/format";
import { Pager } from "@/components/Pager";
import { serverText } from "@/lib/server-text";
import { intlLocale, t, tn, msg } from "@/lib/i18n";
import { holdView } from "@/lib/hold-view";

export const STATUS: Record<OrderStatus, { label: string; tone: "neutral" | "success" | "warning" | "danger" | "info" }> = {
  received: { label: msg("Eingegangen"), tone: "neutral" },
  ready: { label: msg("Wartet (Stornofenster)"), tone: "neutral" },
  on_hold: { label: msg("Zurückgehalten"), tone: "warning" },
  submitted: { label: msg("Übertragen"), tone: "info" },
  in_progress: { label: msg("In Bearbeitung (Picking)"), tone: "info" },
  partially_shipped: { label: msg("Teilversendet"), tone: "info" },
  shipped: { label: msg("Versendet"), tone: "success" },
  cancelled: { label: msg("Storniert"), tone: "neutral" },
  failed: { label: msg("Fehler"), tone: "danger" },
};

/** Zwischenstände, die sich gleich ändern – Liste und Detail fragen dann häufiger nach */
export const TRANSIENT = new Set<OrderStatus>(["received", "ready"]);

/** Anzeige-Status: „ready“ nach Ablauf des Stornofensters heißt „wird übertragen“ */
/** Retouren haben Vorrang vor dem Zustellstatus – sie sind das Letzte, was mit dem Auftrag passiert */
const RETURN_VIEW = {
  returned: { label: msg("Retourniert"), tone: "neutral" as const },
  partially_returned: { label: msg("Teilweise retourniert"), tone: "neutral" as const },
  undeliverable: { label: msg("Rückläufer"), tone: "warning" as const },
};

export function statusView(o: { status: OrderStatus; releaseAt?: string | null; deliveryStatus?: DeliveryStatus | null; returnState?: keyof typeof RETURN_VIEW | null; replaced?: boolean }) {
  // im Shop storniert und neu angefordert: geht als neuer Auftrag weiter
  if (o.status === "cancelled" && o.replaced) return { label: msg("Ersetzt"), tone: "neutral" as const };
  if ((o.status === "shipped" || o.status === "partially_shipped") && o.returnState) return RETURN_VIEW[o.returnState];
  // Track & Trace: nach dem Versand zählt, wo das Paket ist
  if (o.status === "shipped" && o.deliveryStatus && o.deliveryStatus !== "pre_transit") return DELIVERY_VIEW[o.deliveryStatus];
  if (o.status === "ready" && (!o.releaseAt || new Date(o.releaseAt).getTime() <= Date.now())) return { label: msg("Wird übertragen …"), tone: "info" as const };
  if (o.status === "received") return { label: msg("Wird geprüft …"), tone: "neutral" as const };
  return STATUS[o.status];
}

/**
 * Farbe der Status-Pille als Abstufung entlang des Wegs: grau wartet · gelb zurückgehalten · blau bei Logiful ·
 * türkis beim Dienstleister unterwegs · hellgrün in Zustellung · grün zugestellt · rot Problem.
 */
export function pillTone(o: { status: OrderStatus; releaseAt?: string | null; deliveryStatus?: DeliveryStatus | null; returnState?: keyof typeof RETURN_VIEW | null; replaced?: boolean }): PillTone {
  const v = statusView(o);
  // teilversendet: ein Teil ist beim Dienstleister unterwegs
  if (o.status === "partially_shipped" && !o.returnState) return "transit";
  if (o.status === "shipped" && !o.returnState && v.tone !== "danger" && v.tone !== "warning") {
    if (o.deliveryStatus === "delivered") return "success";
    if (o.deliveryStatus === "out_for_delivery") return "delivery";
    return "transit";
  }
  return v.tone;
}

/** kleines Icon je Status (wie Shopify): wartend gestrichelt, Pause, übertragen, in Arbeit, unterwegs, zugestellt … */
export function statusIcon(o: { status: OrderStatus; deliveryStatus?: DeliveryStatus | null; returnState?: keyof typeof RETURN_VIEW | null; replaced?: boolean }) {
  if (o.status === "cancelled" && o.replaced) return <Repeat />;
  if ((o.status === "shipped" || o.status === "partially_shipped") && o.returnState) return o.returnState === "undeliverable" ? <CircleAlert /> : <RotateCcw />;
  if (o.status === "shipped" && o.deliveryStatus && o.deliveryStatus !== "pre_transit") {
    if (o.deliveryStatus === "delivered") return <CircleCheck />;
    if (o.deliveryStatus === "pickup_ready") return <Store />;
    if (o.deliveryStatus === "exception" || o.deliveryStatus === "returned") return <CircleAlert />;
    return <Truck />;
  }
  switch (o.status) {
    case "received":
    case "ready":
      return <CircleDashed />;
    case "on_hold":
      return <CirclePause />;
    case "submitted":
      return <CircleArrowRight />;
    case "partially_shipped":
      return <Truck />;
    case "in_progress":
      return <Loader2 className="motion-safe:animate-[spin_3s_linear_infinite]" />;
    case "shipped":
      return <PackageCheck />;
    case "cancelled":
      return <CircleX />;
    case "failed":
      return <CircleAlert />;
  }
}

const TABS: { key: string | undefined; label: string; statuses?: OrderStatus[] }[] = [
  { key: undefined, label: msg("Alle") },
  { key: "on_hold", label: msg("Zurückgehalten"), statuses: ["on_hold", "failed"] },
  { key: "ready", label: msg("Wartend"), statuses: ["received", "ready"] },
  { key: "submitted", label: msg("Übertragen"), statuses: ["submitted"] },
  { key: "in_progress", label: msg("In Bearbeitung"), statuses: ["in_progress", "partially_shipped"] },
  { key: "shipped", label: msg("Versendet"), statuses: ["shipped"] },
  { key: "cancelled", label: msg("Storniert"), statuses: ["cancelled"] },
];

const PERIODS: { key: string; label: string; days?: number }[] = [
  { key: "", label: msg("Gesamter Zeitraum") },
  { key: "1", label: msg("Letzte 24 Stunden"), days: 1 },
  { key: "7", label: msg("Letzte 7 Tage"), days: 7 },
  { key: "30", label: msg("Letzte 30 Tage"), days: 30 },
  { key: "90", label: msg("Letzte 90 Tage"), days: 90 },
];

/** Klickbare Such-Präfixe für Profis */
const PREFIXES: { prefix: string; label: string }[] = [
  { prefix: "sku:", label: msg("SKU") },
  { prefix: "tracking:", label: msg("Tracking") },
  { prefix: "email:", label: msg("E-Mail") },
  { prefix: "plz:", label: msg("PLZ") },
  { prefix: "land:", label: msg("Land") },
  { prefix: "tag:", label: msg("Tag") },
];

const isoDaysAgo = (d: number) => new Date(Date.now() - d * 86_400_000).toISOString().slice(0, 10);

export function OrdersPage() {
  const { tenantId, role } = useTenant();
  const mayEdit = usePermission()("orders");
  // frei wählbare Spalten (je Browser); Empfänger-Spalten stehen vor dem Status
  const [cols, setCols] = useColumnChoice<OrderColumnKey>("lc-order-cols", ORDER_COLUMN_KEYS, ORDER_COLUMN_DEFAULTS);
  const shown = ORDER_COLUMN_KEYS.filter((k) => cols.includes(k));
  const beforeStatus = shown.filter((k) => k === "recipient" || k === "country" || k === "email");
  const afterStatus = shown.filter((k) => !(beforeStatus as OrderColumnKey[]).includes(k));
  // Sammelaktionen: Auswahl auf der Seite oder alle Treffer des Filters (max. 500)
  const [selected, setSelected] = useState<Set<string>>(() => new Set());
  const [allMatches, setAllMatches] = useState<{ ids: string[]; statuses: Record<string, OrderStatus>; total: number; capped: boolean } | null>(null);
  const [exporting, setExporting] = useState(false);
  const methodsQ = useQuery({ queryKey: ["shipping-methods", tenantId], queryFn: () => api.backend.shippingMethods(tenantId), staleTime: 10 * 60_000, enabled: cols.includes("shippingMethod") });
  const methodName = (id: string | null | undefined) => {
    if (!id) return null;
    for (const w of methodsQ.data?.warehouses ?? []) { const m = w.shippingMethods.find((x) => x.id === id); if (m) return m.label || m.name; }
    return id;
  };
  const [params, setParams] = useSearchParams();
  const tab = params.get("status") ?? undefined;
  const channelId = params.get("channel") ?? undefined;
  const country = params.get("country") ?? undefined;
  const period = params.get("period") ?? "";
  const partial = params.get("partial") === "open" ? ("open" as const) : undefined;
  const showReplaced = params.get("replaced") === "1" ? ("1" as const) : undefined;
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const hold = (params.get("hold") as HoldKind | null) ?? undefined;
  const openId = params.get("open");
  const urlQ = params.get("q") ?? "";
  const [input, setInput] = useState(urlQ);
  const inputRef = useRef<HTMLInputElement>(null);
  const rowRefs = useRef<(HTMLTableRowElement | null)[]>([]);
  const [focused, setFocused] = useState(false);
  const [creating, setCreating] = useState(false);
  const [csvOpen, setCsvOpen] = useState(false);
  const qc = useQueryClient();

  const update = (patch: Record<string, string | null | undefined>, replace = true) => {
    const next = new URLSearchParams(params);
    for (const [k, v] of Object.entries(patch)) v ? next.set(k, v) : next.delete(k);
    // neue Suche/Filter → zurück auf Seite 1
    if (Object.keys(patch).some((k) => k !== "open" && k !== "page")) next.delete("page");
    setParams(next, { replace });
  };
  const page = Math.max(1, Number(params.get("page")) || 1);
  const pageSize = ([50, 100, 250].includes(Number(params.get("size"))) ? Number(params.get("size")) : 100) as 50 | 100 | 250;
  // Stand der ersten Seite: beim Weiterblättern verschieben neu eingehende Aufträge die Seiten nicht
  const asOfRef = useRef<string | null>(null);
  const setOpen = (id: string | null) => update({ open: id }, false);

  // Eingabe entprellt in die URL übernehmen – Suchen sind teilbar und überleben Neuladen
  useEffect(() => {
    if (input === urlQ) return;
    const t = setTimeout(() => update({ q: input.trim() || null }), 200);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [input]);
  useEffect(() => setInput((cur) => (cur.trim() === urlQ ? cur : urlQ)), [urlQ]);

  // "/" springt in die Suche – auch für Barcode-Scanner, die Enter senden
  useEffect(() => {
    const onKey = (e: globalThis.KeyboardEvent) => {
      const tag = (e.target as HTMLElement).tagName;
      if (e.key === "/" && tag !== "INPUT" && tag !== "TEXTAREA" && tag !== "SELECT") {
        e.preventDefault();
        inputRef.current?.focus();
        inputRef.current?.select();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  const statuses = TABS.find((t) => t.key === tab)?.statuses;
  const query: OrderSearchQuery = {
    q: urlQ || undefined,
    status: statuses?.join(","),
    channelId,
    country,
    partial,
    hold,
    replaced: showReplaced,
    from: PERIODS.find((p) => p.key === period)?.days ? isoDaysAgo(PERIODS.find((p) => p.key === period)!.days!) : undefined,
    page,
    pageSize,
    ...(page > 1 && asOfRef.current ? { until: asOfRef.current } : {}),
  };
  const q = useQuery({
    queryKey: ["orders-search", tenantId, query],
    queryFn: () => api.orders.search(tenantId, query),
    placeholderData: keepPreviousData,
    // live: solange Aufträge gerade verarbeitet werden alle 3 s, sonst alle 15 s
    refetchInterval: (qq) => (qq.state.data?.items.some((o) => TRANSIENT.has(o.status)) ? 3000 : 15000),
  });
  const items = q.data?.items ?? [];
  const filterKey = JSON.stringify({ ...query, page: undefined, until: undefined });
  useEffect(() => { setSelected(new Set()); setAllMatches(null); }, [filterKey]);
  const selectedIds = allMatches ? allMatches.ids : [...selected];
  const statusById = new Map(items.map((o) => [o.id, o.status]));
  const labelById = new Map(items.map((o) => [o.id, o.orderNumber]));
  const pageAllSelected = items.length > 0 && items.every((o) => selected.has(o.id));
  const toggle = (id: string) => { setAllMatches(null); setSelected((cur) => { const n = new Set(cur); n.has(id) ? n.delete(id) : n.add(id); return n; }); };
  const togglePage = () => { setAllMatches(null); setSelected((cur) => { const n = new Set(cur); if (pageAllSelected) items.forEach((o) => n.delete(o.id)); else items.forEach((o) => n.add(o.id)); return n; }); };
  const selectAllMatches = async () => {
    const r = await api.orders.matchIds(tenantId, { ...query, page: undefined, until: undefined });
    setAllMatches(r);
  };
  const cellCtx = { needles: [] as string[], methodName };
  // CSV der Auswahl mit den sichtbaren Spalten (Semikolon + BOM für Excel)
  const exportCsv = async () => {
    setExporting(true);
    try {
      let rows: OrderItem[] = items.filter((o) => selectedIds.includes(o.id));
      if (allMatches) {
        rows = [];
        for (let p = 1; rows.length < allMatches.ids.length && p <= 2; p++) rows.push(...(await api.orders.search(tenantId, { ...query, page: p, pageSize: 250, until: undefined })).items);
        rows = rows.filter((o) => allMatches.ids.includes(o.id));
      }
      const esc = (v: string) => (/[";\n]/.test(v) ? `"${v.replace(/"/g, '""')}"` : v);
      const head = [t("Auftrag"), t("Status"), ...shown.map((k) => t(ORDER_COLUMN_BY_KEY[k].label))];
      const lines = rows.map((o) => [o.orderNumber, statusView(o).label, ...shown.map((k) => ORDER_COLUMN_BY_KEY[k].text(o, cellCtx))].map(esc).join(";"));
      const blob = new Blob(["\uFEFF" + [head.map(esc).join(";"), ...lines].join("\r\n")], { type: "text/csv;charset=utf-8" });
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = `auftraege-${new Date().toISOString().slice(0, 10)}.csv`;
      a.click();
      URL.revokeObjectURL(a.href);
    } finally {
      setExporting(false);
    }
  };
  const counts = q.data?.counts ?? {};
  useEffect(() => { if (page === 1 && q.data?.asOf) asOfRef.current = q.data.asOf; }, [page, q.data?.asOf]);
  const goPage = (n: number) => { update({ page: n > 1 ? String(n) : null }, false); window.scrollTo({ top: 0, behavior: "smooth" }); };
  const countFor = (t: (typeof TABS)[number]) => (t.statuses ? t.statuses.reduce((s, k) => s + (counts[k] ?? 0), 0) : Object.values(counts).reduce((s, n) => s + (n ?? 0), 0));

  const openWhenSingle = useRef<string | null>(null);
  useEffect(() => {
    if (!openWhenSingle.current || q.isPlaceholderData || q.isFetching || openWhenSingle.current !== urlQ) return;
    openWhenSingle.current = null;
    if (items.length === 1) setOpen(items[0]!.id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [q.isPlaceholderData, q.isFetching, urlQ, items]);

  const needles = useMemo(() => {
    const p = parseOrderQuery(urlQ);
    return [...p.terms, ...Object.values(p.fields).flat()].map((x) => x.toLowerCase());
  }, [urlQ]);
  const searching = !!urlQ;
  const filtered = searching || !!channelId || !!country || !!period || !!partial || !!hold;
  const resetAll = () => {
    setInput("");
    setParams(openId ? { open: openId } : {}, { replace: true });
  };

  const focusRow = (i: number) => rowRefs.current[Math.max(0, Math.min(items.length - 1, i))]?.focus();
  const onInputKey = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Escape") {
      if (input) { e.preventDefault(); setInput(""); update({ q: null }); } else inputRef.current?.blur();
    }
    if (e.key === "ArrowDown" && items.length) { e.preventDefault(); focusRow(0); }
    // Eindeutiger Treffer (z. B. gescannte Trackingnummer) → direkt öffnen
    if (e.key === "Enter") {
      // Scanner tippen schneller als die Entprellung: Suche sofort starten und den Treffer öffnen, sobald er da ist
      if (input.trim() !== urlQ) { openWhenSingle.current = input.trim(); update({ q: input.trim() || null }); }
      else if (q.isPlaceholderData || q.isFetching) openWhenSingle.current = urlQ;
      else if (items.length === 1) setOpen(items[0]!.id);
      else if (items.length) focusRow(0);
    }
  };
  const onRowKey = (e: KeyboardEvent<HTMLTableRowElement>, i: number, id: string) => {
    if (e.key === "ArrowDown") { e.preventDefault(); focusRow(i + 1); }
    if (e.key === "ArrowUp") { e.preventDefault(); i === 0 ? inputRef.current?.focus() : focusRow(i - 1); }
    if (e.key === "Enter" || e.key === " ") { e.preventDefault(); setOpen(id); }
  };
  const addPrefix = (prefix: string) => {
    const next = `${input.trim()}${input.trim() ? " " : ""}${prefix}`;
    setInput(next);
    requestAnimationFrame(() => inputRef.current?.focus());
  };

  const channels = q.data?.facets.channels ?? [];
  const countries = q.data?.facets.countries ?? [];
  const selectCls = "h-8 rounded-lg border border-border bg-surface px-2.5 text-[12.5px] text-fg hover:border-border-strong focus:outline-none focus-visible:ring-2 focus-visible:ring-ink/15";

  return (
    <div className="pb-16">
      <PageHeader title={t("Aufträge")} description={t("Von der Bestellung im Shop bis zur Trackingnummer – mit Begründung, wenn einer zurückgehalten wird.")} actions={mayEdit ? <div className="flex flex-wrap gap-2"><OrderHistoryImportButton /><Button icon={<FileUp className="size-4" />} onClick={() => setCsvOpen(true)}>{t("CSV importieren")}</Button><Button variant="primary" icon={<Plus className="size-4" />} onClick={() => setCreating(true)}>{t("Auftrag anlegen")}</Button></div> : undefined} />
      <div className="px-4 md:px-8 space-y-3">
        <OrderHistoryImportBanner />
        <OrderKpiStrip onSelect={(k) => k === "on_hold" && update({ status: "on_hold" })} />
        <div className="relative">
          <Input
            ref={inputRef}
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={onInputKey}
            onFocus={() => setFocused(true)}
            onBlur={() => setTimeout(() => setFocused(false), 150)}
            placeholder={t("Suchen: Auftragsnr., Name, E-Mail, PLZ, SKU, Trackingnummer …")}
            aria-label={t("Aufträge durchsuchen")}
            className="h-11 text-[14px]"
            leading={q.isFetching && searching ? <Loader2 className="size-4 animate-spin" /> : <Search className="size-4" />}
            suffix={input ? (
              <button onClick={() => { setInput(""); update({ q: null }); inputRef.current?.focus(); }} className="size-6 grid place-items-center rounded-md text-fg-subtle hover:bg-surface-2 hover:text-fg" aria-label={t("Suche leeren")}><X className="size-3.5" /></button>
            ) : <Kbd>/</Kbd>}
          />
          {focused && !input && (
            <div className="mt-2 flex flex-wrap items-center gap-1.5 text-[12px] text-fg-muted animate-fade-in">
              <span>{t("Gezielt suchen:")}</span>
              {PREFIXES.map((p) => (
                <button key={p.prefix} onMouseDown={(e) => e.preventDefault()} onClick={() => addPrefix(p.prefix)} className="h-6 px-2 rounded-md border border-border bg-surface hover:border-ink font-mono text-[11.5px]">{p.prefix}</button>
              ))}
              <span className="text-fg-subtle">{t("· mehrere Begriffe = alle müssen passen · „in Anführungszeichen“ = genau so · Enter öffnet einen eindeutigen Treffer")}</span>
            </div>
          )}
        </div>

        <div className="flex flex-wrap items-center gap-2">
          <div className="inline-flex p-0.5 rounded-lg bg-surface-2 border border-border overflow-x-auto max-w-full">
            {TABS.map((tb) => {
              const n = countFor(tb);
              if (tb.key === "cancelled" && !n && tab !== "cancelled") return null;
              return (
                <button
                  key={tb.label}
                  onClick={() => update({ status: tb.key ?? null })}
                  className={cx("h-7 px-3 rounded-md text-[12.5px] font-medium flex items-center gap-1.5 whitespace-nowrap", tab === tb.key ? "bg-surface shadow-sm" : "text-fg-muted hover:text-fg")}
                >
                  {t(tb.label)} <span className={cx("tabular-nums", tb.key === "on_hold" && n ? "text-warning" : "text-fg-subtle")}>{q.data ? n : "·"}</span>
                </button>
              );
            })}
          </div>
          <div className="flex flex-wrap items-center gap-2 ml-auto">
            {(partial || (q.data?.openPartials ?? 0) > 0) && (
              <button
                onClick={() => update({ partial: partial ? null : "open" })}
                aria-pressed={!!partial}
                title={t("Restlieferungen aus Teillieferungen, die noch nicht versendet sind")}
                className={cx("h-8 px-2.5 rounded-lg border text-[12.5px] font-medium inline-flex items-center gap-1.5", partial ? "border-ink bg-ink text-ink-fg" : "border-border hover:bg-surface-2")}
              >
                <Split className="size-3.5 text-fg-muted" /> {t("Offene Teillieferungen")} <span className="tabular-nums text-fg-subtle">{q.data?.openPartials ?? "·"}</span>
              </button>
            )}
            {(showReplaced || (q.data?.replacedCount ?? 0) > 0) && (
              <button
                onClick={() => update({ replaced: showReplaced ? null : "1" })}
                aria-pressed={!!showReplaced}
                title={t("Im Shop stornierte und neu angeforderte Fulfillment-Anfragen als eigene Zeilen zeigen (sonst unter dem aktuellen Auftrag)")}
                className={cx("h-8 px-2.5 rounded-lg border text-[12.5px] font-medium inline-flex items-center gap-1.5", showReplaced ? "border-ink bg-ink text-ink-fg" : "border-border hover:bg-surface-2")}
              >
                <Repeat className="size-3.5 text-fg-muted" /> {t("Ersetzte anzeigen")} <span className="tabular-nums text-fg-subtle">{q.data?.replacedCount ?? "·"}</span>
              </button>
            )}
            {(tab === "on_hold" || hold) && (
              <select aria-label={t("Grund")} value={hold ?? ""} onChange={(e) => update({ hold: e.target.value || null })} className={cx(selectCls, hold && "border-ink")}>
                <option value="">{t("Alle Gründe")}</option>
                {(Object.keys(HOLD_KIND_LABEL) as HoldKind[]).filter((k) => k !== "test_mode" || hold === "test_mode").map((k) => <option key={k} value={k}>{HOLD_KIND_LABEL[k]}</option>)}
              </select>
            )}
            {channels.length > 1 && (
              <select aria-label={t("Shop")} value={channelId ?? ""} onChange={(e) => update({ channel: e.target.value || null })} className={selectCls}>
                <option value="">{t("Alle Shops")}</option>
                {channels.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
              </select>
            )}
            {countries.length > 1 && (
              <select aria-label={t("Land")} value={country ?? ""} onChange={(e) => update({ country: e.target.value || null })} className={selectCls}>
                <option value="">{t("Alle Länder")}</option>
                {countries.map((c) => <option key={c} value={c}>{countryName(c)}</option>)}
              </select>
            )}
            <select aria-label={t("Zeitraum")} value={period} onChange={(e) => update({ period: e.target.value || null })} className={selectCls}>
              {PERIODS.map((p) => <option key={p.key} value={p.key}>{t(p.label)}</option>)}
            </select>
            <ColumnPicker columns={ORDER_COLUMNS} value={cols} onChange={setCols} defaults={ORDER_COLUMN_DEFAULTS} />
          </div>
        </div>

        {filtered && q.data && (
          <div className="flex items-center gap-2 text-[12.5px] text-fg-muted">
            <span>
              <b className="text-fg tabular-nums">{q.data.total}</b> {q.data.total === 1 ? t("Treffer") : t("Treffer")}
              {searching && <> {t("für „")}{urlQ}“</>}

            </span>
            <button onClick={resetAll} className="font-medium text-fg hover:underline">{t("Zurücksetzen")}</button>
          </div>
        )}

        {selectedIds.length > 0 && (
          <>
            <OrderBulkBar
              ids={selectedIds}
              statusOf={(id) => allMatches?.statuses[id] ?? statusById.get(id)}
              labelOf={(id) => labelById.get(id) ?? id.slice(0, 8)}
              mayEdit={mayEdit}
              mayManage={canManage(role)}
              onClear={() => { setSelected(new Set()); setAllMatches(null); }}
              onExport={() => void exportCsv()}
              exporting={exporting}
            />
            {!allMatches && pageAllSelected && (q.data?.total ?? 0) > items.length && (
              <p className="text-[12.5px] text-fg-muted">
                {t("Alle Aufträge dieser Seite sind ausgewählt.")}{" "}
                <button onClick={() => void selectAllMatches()} className="font-medium text-fg hover:underline">{tn(Math.min(q.data!.total, 500), "Alle {n} Treffer auswählen", "Alle {n} Treffer auswählen")}</button>
              </p>
            )}
            {allMatches?.capped && <p className="text-[12.5px] text-warning">{tn(allMatches.total, "Von {n} Treffern sind die neuesten 500 ausgewählt – größere Mengen bitte in Etappen.", "Von {n} Treffern sind die neuesten 500 ausgewählt – größere Mengen bitte in Etappen.")}</p>}
          </>
        )}

        <Card className="overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full min-w-[760px] text-left">
              <thead className="text-[12px] text-fg-subtle border-b border-border bg-surface-2/50">
                <tr>
                  <th className="h-9 w-9 pl-4"><input type="checkbox" checked={pageAllSelected} onChange={togglePage} aria-label={t("Alle auf dieser Seite auswählen")} className="size-3.5 accent-[var(--color-ink)] cursor-pointer align-middle" /></th>
                  <th className="h-9 pl-2 pr-3 font-medium">{t("Auftrag")}</th>
                  {beforeStatus.map((k) => <th key={k} className={cx("h-9 px-3 font-medium", ORDER_COLUMN_BY_KEY[k].align === "right" && "text-right")}>{t(ORDER_COLUMN_BY_KEY[k].label)}</th>)}
                  <th className="h-9 px-3 font-medium">{t("Status")}</th>
                  {afterStatus.map((k, i) => <th key={k} className={cx("h-9 px-3 font-medium", ORDER_COLUMN_BY_KEY[k].align === "right" && "text-right", i === afterStatus.length - 1 && "pr-4")}>{t(ORDER_COLUMN_BY_KEY[k].label)}</th>)}
                </tr>
              </thead>
              <tbody className={cx(q.isPlaceholderData && "opacity-60 transition-opacity")}>
                {q.isLoading && Array.from({ length: 8 }, (_, i) => (
                  <tr key={i} className="border-b border-border"><td colSpan={3 + shown.length} className="px-4 py-3"><Skeleton className="h-4 w-full" /></td></tr>
                ))}
                {items.map((o, i) => (
                  <Fragment key={o.id}>
                  <tr
                    ref={(el) => { rowRefs.current[i] = el; }}
                    tabIndex={0}
                    onClick={() => setOpen(o.id)}
                    onKeyDown={(e) => onRowKey(e, i, o.id)}
                    className={cx("group border-b border-border last:border-0 cursor-pointer transition-colors outline-none focus-visible:bg-surface-2 focus-visible:shadow-[inset_2px_0_0_var(--color-ink)]", openId === o.id ? "bg-surface-2 shadow-[inset_2px_0_0_var(--color-ink)]" : "hover:bg-surface-2/50")}
                  >
                    <td className="pl-4 py-2.5 w-9" onClick={(e) => e.stopPropagation()}><input type="checkbox" checked={allMatches ? allMatches.ids.includes(o.id) : selected.has(o.id)} onChange={() => toggle(o.id)} aria-label={`${o.orderNumber} ${t("auswählen")}`} className="size-3.5 accent-[var(--color-ink)] cursor-pointer align-middle" /></td>
                    <td className="pl-2 pr-3 py-2.5">
                      <div className="font-medium tabular-nums whitespace-nowrap flex items-center gap-1.5"><Highlight text={o.orderNumber} needles={needles} />{o.testMode && <Badge tone="warning">{t("Test")}</Badge>}{!!o.openRests?.length && <Tip content={t("Restlieferung noch offen: {list}", { list: o.openRests.join(", ") })}><span><Badge tone="warning" icon={<Split className="size-3" />}>{t("Rest offen")}</Badge></span></Tip>}{o.replaced && o.shopGroup?.replacedBy && <Tip content={t("Im Shop neu angefordert – geht weiter als {n}", { n: o.shopGroup.replacedBy })}><span><Badge tone="neutral" icon={<Repeat className="size-3" />}>{t("→ {n}", { n: o.shopGroup.replacedBy })}</Badge></span></Tip>}{!o.replaced && (o.shopGroup?.count ?? 0) > 1 && <Tip content={t("{n} Fulfillment-Anfragen zu dieser Shop-Bestellung: {list}", { n: o.shopGroup!.count, list: o.shopGroup!.numbers.join(", ") })}><span><Badge tone="info" icon={<Layers className="size-3" />}>{t("{n} Anfragen", { n: o.shopGroup!.count })}</Badge></span></Tip>}{!o.replaced && !!o.shopGroup?.earlier?.length && (
                        <button
                          type="button"
                          onClick={(e) => { e.stopPropagation(); setExpanded((cur) => { const n = new Set(cur); n.has(o.id) ? n.delete(o.id) : n.add(o.id); return n; }); }}
                          aria-expanded={expanded.has(o.id)}
                          className="inline-flex items-center gap-0.5 rounded-md px-1.5 py-0.5 text-[11px] font-medium text-fg-muted hover:bg-surface-2 hover:text-fg"
                        >
                          <Repeat className="size-3" />
                          {tn(o.shopGroup.earlier.length, "1 frühere Anfrage", "{n} frühere Anfragen")}
                          <ChevronDown className={cx("size-3 transition-transform", expanded.has(o.id) && "rotate-180")} />
                        </button>
                      )}{o.addressCorrected && <Tip content={t("Lieferadresse wurde geändert (Bereinigung, DHL-Leitdaten, Logiful, Endkunde oder Portal) – jeder Schritt im Auftrag")}><span><Badge tone="info" icon={<MapPinCheck className="size-3" />}>{t("Adresse korrigiert")}</Badge></span></Tip>}</div>
                      <div className="text-[11.5px] text-fg-subtle whitespace-nowrap flex items-center gap-1"><ChannelMark kind={o.channelKind} />{o.channelName} ·{" "}
                        {o.lines?.length ? (
                          <ItemsPopover label={tn(o.itemCount, "1 Artikel", "{n} Artikel")} lines={o.lines} more={o.itemCount > o.lines.reduce((s, l) => s + l.quantity, 0)} header={<span className="text-[13px] font-semibold">{t("Logiful Fulfillment")}</span>} />
                        ) : tn(o.itemCount, "1 Artikel", "{n} Artikel")}
                      </div>
                    </td>
                    {beforeStatus.map((k) => <td key={k} className={cx("px-3 py-2.5 whitespace-nowrap", ORDER_COLUMN_BY_KEY[k].align === "right" && "text-right")}>{ORDER_COLUMN_BY_KEY[k].cell(o, { needles, methodName })}</td>)}
                    <td className="px-3 py-2.5"><OrderStatusCell o={o} /></td>
                    {afterStatus.map((k, i) => <td key={k} className={cx("px-3 py-2.5", ORDER_COLUMN_BY_KEY[k].align === "right" && "text-right", i === afterStatus.length - 1 && "pr-4")}>{ORDER_COLUMN_BY_KEY[k].cell(o, { needles, methodName })}</td>)}
                  </tr>
                  {expanded.has(o.id) && o.shopGroup?.earlier?.map((e) => (
                    <tr key={e.id} onClick={() => setOpen(e.id)} className="border-b border-border bg-surface-2/30 cursor-pointer hover:bg-surface-2/60 text-[12.5px]">
                      <td />
                      <td colSpan={2 + shown.length} className="pl-2 pr-4 py-1.5">
                        <span className="inline-flex flex-wrap items-center gap-x-2 text-fg-muted">
                          <span className="text-fg-subtle">↳</span>
                          <span className="font-medium tabular-nums text-fg">{e.orderNumber}</span>
                          <StatusPill tone="neutral" icon={<Repeat />}>{t("Ersetzt")}</StatusPill>
                          <span>{tn(e.itemCount, "1 Artikel", "{n} Artikel")} · {t("angefordert {d}", { d: new Date(e.createdAt).toLocaleString(intlLocale(), { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" }) })}</span>
                          <span className="text-fg-subtle">{t("im Shop storniert und als {n} neu angefordert", { n: o.orderNumber })}</span>
                        </span>
                      </td>
                    </tr>
                  ))}
                  </Fragment>
                ))}
              </tbody>
            </table>
          </div>
          {q.data && q.data.total > 0 && (
            <Pager total={q.data.total} page={page} pageSize={pageSize} onPage={goPage} sizes={[50, 100, 250]} onPageSize={(n) => update({ size: n === 100 ? null : String(n) })} />
          )}
          {page > 1 && asOfRef.current && <p className="px-4 pb-2.5 text-[11.5px] text-fg-subtle">{t("Stand")} {new Date(asOfRef.current).toLocaleTimeString(intlLocale(), { hour: "2-digit", minute: "2-digit" })} {t("Uhr – neuere Aufträge stehen auf Seite 1.")}</p>}
          {q.isError && <p className="px-4 py-6 text-[13px] text-danger">{t("Suche fehlgeschlagen. Bitte erneut versuchen.")}</p>}
          {!q.isLoading && !q.isError && items.length === 0 && (
            filtered ? (
              <EmptyState icon={<SearchX className="size-5" />} title={searching ? t("Nichts gefunden für „{urlQ}“", { urlQ }) : t("Keine Aufträge mit diesen Filtern")} action={<Button onClick={resetAll}>{t("Suche und Filter zurücksetzen")}</Button>}>
                {t("Gesucht wird in Auftragsnr., Name, Firma, E-Mail, Telefon, Adresse, SKU, Artikelname, Trackingnummer und Lager-Referenz. Weniger Begriffe oder einen anderen Zeitraum probieren.")}
              </EmptyState>
            ) : (
              <EmptyState icon={<ShoppingCart className="size-5" />} title={tab ? t("Keine Aufträge in diesem Status") : t("Noch keine Aufträge")}>
                {tab === "on_hold" ? t("Kein Auftrag wartet auf Klärung.") : t("Sobald in deinem Shop bestellt wird, erscheinen die Aufträge hier.")}
              </EmptyState>
            )
          )}
        </Card>
        {items.length > 0 && <p className="text-[11.5px] text-fg-subtle hidden md:block"><Kbd>/</Kbd> {t("Suche ·")} <Kbd>↑</Kbd><Kbd>↓</Kbd> {t("blättern ·")} <Kbd>{t("Enter")}</Kbd> {t("öffnen ·")} <Kbd>{t("Esc")}</Kbd> {t("leeren")}</p>}
        <CsvImportDialog kind="orders" open={csvOpen} onOpenChange={setCsvOpen} onDone={() => void qc.invalidateQueries({ queryKey: ["orders-search"] })} />
      <OrderSheet orderId={openId} onClose={() => setOpen(null)} />
        <ManualOrderSheet open={creating} onOpenChange={setCreating} onCreated={(id) => setOpen(id)} />
      </div>
    </div>
  );
}

/**
 * Status in der Liste. Zurückgehalten: Pille nennt gleich den Grund („Wartet auf Ware“, „Adresse prüfen“ …), darunter
 * das Wesentliche (Artikelname statt EAN), voller Wortlaut im Tooltip – statt „Zurückgehalten“ + abgeschnittenem Satz.
 */
function OrderStatusCell({ o }: { o: OrderItem }) {
  const h = o.status === "on_hold" ? holdView(o.holdReasons, o.lines ?? []) : null;
  if (!h) return <StatusPill tone={pillTone(o)} icon={statusIcon(o)}>{t(statusView(o).label)}</StatusPill>;
  return (
    <Tip content={<span className="whitespace-pre-line">{h.full}</span>}>
      <span className="inline-flex flex-col items-start gap-0.5 cursor-help max-w-[240px]">
        <span className="inline-flex items-center gap-1">
          <StatusPill tone={h.tone} icon={statusIcon(o)}>{h.label}</StatusPill>
          {h.more > 0 && <span className="text-[11px] font-medium text-fg-muted tabular-nums">+{h.more}</span>}
        </span>
        {h.detail && <span className="block max-w-full truncate pl-0.5 text-[11.5px] text-fg-muted">{h.detail}</span>}
      </span>
    </Tip>
  );
}
